import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ProjectStatus } from "@enums/projectStatus.enum";
import { SalesEnquiryStatus } from "@enums/salesEnquiryStatus.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Client } from "@interfaces/models/client.interface";
import { Project } from "@interfaces/project.interface";
import { SalesEnquiry } from "@interfaces/models/salesEnquiry.interface";
import { ClientService } from "@services/client.service";
import { ProjectService } from "@services/project.service";
import { PurchaseLogService } from "@services/purchaseLog.service";
import { SalesEnquiryService } from "@services/salesEnquiry.service";
import { SalesOrderService } from "@services/salesOrder.service";
import { UserProfileService } from "@services/userProfile.service";
import { WorkOrderService } from "@services/workOrder.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "projects-id",
	styleUrls: ["projects-id.css"],
	templateUrl: "projects-id.html"
})
export class ProjectIdAdminComponent {
	activeProcessIds: number[] = [];
	clients: Client[] = [];
	createEnabled = true;
	editEnabled = true;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	item: Project = new Project();
	itemId = "";
	itemType = "Project";
	projectStatuses: any;
	purchaseLogs: any[] = [];
	quotesQuantity = 0;
	quotesTotalParts = 0;
	quotesTotalValue = 0;
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	salesEnquiries: SalesEnquiry[] = [];
	salesOrders: any[] = [];
	salesQuantity = 0;
	salesTotalParts = 0;
	salesTotalValue = 0;
	statuses: any;
	title: string = "View " + this.itemType;
	userProfiles: any[] = [];
	workOrders: any[] = [];

	constructor(private router:Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: ProjectService, private clientService: ClientService, private purchaseLogService: PurchaseLogService, private salesEnquirieService: SalesEnquiryService, private salesOrderService: SalesOrderService, private userProfileService: UserProfileService, private workOrderService: WorkOrderService) {
		this.buildButtons("view");
		this.getClients();
		this.getUserProfiles();
		this.projectStatuses = GetPropertiesFromEnum(ProjectStatus);

		this.route.params
			.subscribe((params) => {
				if (params.id != "create") {
					if (params.id.split("_").length > 1) {
						this.getItem(params.id.split("_")[0]);
						this.edit();
					} else {
						this.getItem(params.id);
					}
				} else {
					this.create();
				}
			});

		this.viewPurchaseLog = this.viewPurchaseLog.bind(this);
		this.viewSalesEnquiry = this.viewSalesEnquiry.bind(this);
		this.viewSalesOrder = this.viewSalesOrder.bind(this);
		this.viewWorkOrder = this.viewWorkOrder.bind(this);
	}

	boolYesNo(cellInfo: any){		
		if(cellInfo.value === true)
		{
			return "YES";
		}
		else
		{
			return "NO";
		}
	} 

	buildButtons(mode: string) {
		if (mode == "edit" && this.editEnabled) {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "create" && this.createEnabled) {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
			if (this.item.projectStatus !== ProjectStatus.Complete) {
				this.headerSecondaryButtons = [
					{ method: "markComplete", text: "Mark Complete" }
				];
			}
		}
	}

	cancelEditing() {
		if (confirm("Are you sure you wish to switch modes? Unsaved changes will be discarded.") == false) {
			return;
		}
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	create() {
		this.readOnly = false;
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
	}

	createItem() {
		this.item.projectStatus = ProjectStatus.New;
		this.itemService.createSingle(this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated " + this.itemType);
					this.item = new Project();
					this.close();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.warn("You are now editing...");
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) => this.clients = res.response.items,
				(err) => console.log(err)
			);
	}
	
	getItem(itemId: number) {
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.getPurchaseLogs(itemId);
					this.getSalesEnquiries(itemId);
					this.getSalesOrders(itemId);
					this.getWorkOrders(itemId);
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getPurchaseLogs(itemId: number) {
		this.purchaseLogService.getByProjectId(itemId)
			.subscribe(
				(res: any) => this.purchaseLogs = res.response,
				(err) => console.log(err)
			);
	}

	getSalesEnquiries(itemId: number) {
		this.salesEnquirieService.getByProjectId(itemId)
			.subscribe(
				(res: any) => {
					this.salesEnquiries = res.response;
					// Set Quote (non-lost Sales Enquiry) Stats
					const quotes = this.salesEnquiries.filter((d: SalesEnquiry) => (d.salesEnquiryStatus === SalesEnquiryStatus.Ongoing || d.salesEnquiryStatus === SalesEnquiryStatus.Won));
					this.quotesQuantity = quotes.length;
					quotes.forEach((salesEnquiry) => {
						this.quotesTotalParts += salesEnquiry.enquiryQuantity;
						this.quotesTotalValue += Math.round(salesEnquiry.enquiryValue * 10000);
					});
					this.quotesTotalValue = this.quotesTotalValue / 10000;
				},
				(err) => console.log(err)
			);
	}

	getSalesOrders(itemId: number) {
		this.salesOrderService.forProjectId(itemId)
			.subscribe(
				(res: any) => {
					this.salesOrders = res.response;
					// Set Sales Order Stats
					this.salesQuantity = this.salesOrders.length;
					this.salesOrders.forEach((salesOrder) => {
						this.salesTotalParts += salesOrder.quantity;
						this.salesTotalValue += Math.round(salesOrder.totalValue * 10000);
					});
					this.salesTotalValue = this.salesTotalValue / 10000;
				},
				(err) => console.log(err)
			);
	}

	getUserProfiles() {
		this.userProfileService.getAll()
			.subscribe(
				(res: any) => {
					this.userProfiles = res.response.items;
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getWorkOrders(itemId: number) {
		this.workOrderService.forProjectId(itemId)
			.subscribe(
				(res: any) => this.workOrders = res.response,
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	markComplete() {
		if (confirm("Are you sure you wish to mark this project as complete? It will be reopened if a new Sales Order is assigned to it.") == false) {
			return;
		}
		this.itemService.markComplete(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Successfully Marked Complete");
					this.getItem(this.item.id);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	updateItem() {
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					() => this.toastService.success("Successfully Updated");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	viewPurchaseLog(e: any)
	{
		this.router.navigate([`/admin/purchases/${e.row.data.lookupId}`]);
	}

	viewSalesEnquiry(e: any)
	{
		this.router.navigate([`/admin/salesEnquiries/${e.row.data.lookupId}`]);
	}

	viewSalesOrder(e: any)
	{
		this.router.navigate([`/admin/salesOrders/${e.row.data.lookupId}`]);
	}

	viewWorkOrder(e: any)
	{
		this.router.navigate([`/admin/workOrders/${e.row.data.id}`]);
	}
}